<template>
    <span v-if="visibleCreateActions.length > 0">
        <prime-button
            label="Create Asset"
            icon="pi pi-chevron-down"
            iconPos="right"
            class="create-asset-button"
            data-test-id="create-asset-button"
            @click="toggleMenu"
        />
        <prime-tiered-menu
            ref="createMenu"
            data-test-id="create-asset-menu"
            :model="visibleCreateActions"
            :pt="{
                item: ({ context }) => ({
                    'data-test-id': context.item.testId,
                }),
                itemLabel: {
                    style: 'white-space: nowrap',
                },
            }"
            popup
        >
            <template #itemicon="{ item, class: iconClass }">
                <img
                    v-if="item.image"
                    :src="item.image"
                    :class="iconClass"
                    class="create-asset-menu__image-icon"
                    :data-test-id="`${item.testId}-icon`"
                    alt=""
                />
            </template>
        </prime-tiered-menu>
    </span>
</template>

<script>
import PrimeButton from 'primevue/button'
import PrimeTieredMenu from 'primevue/tieredmenu'
import featureFlagConstants from '@/constants/feature-flags'
import Permissions from '@/mixins/permissions'
import relayTypes from '@/constants/relay-types'
import radioTypes from '@/constants/radio-types.js'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'

export default {
    components: {
        PrimeButton,
        PrimeTieredMenu,
    },

    mixins: [Permissions, needsOrganizationSlug],

    data() {
        return {
            // Grouped into submenus. In a TieredMenu a top-level item with an
            // `items` array becomes a parent entry whose children flyout on
            // hover/focus, so each section is its own submenu.
            createActionGroups: [
                {
                    label: 'Relays',
                    testId: 'create-relays-submenu',
                    items: [
                        {
                            label: 'Create EDGE Relay',
                            image: '/images/assets/icon-relay-edge.svg',
                            organizationFeatures: ['ALLOW_RELAYS'],
                            permission: 'create assets',
                            testId: 'create-edge-relay',
                            command: () =>
                                this.$router.push(
                                    `/${this.organizationSlug}/relays/create?type=${relayTypes.EDGE.type}`
                                ),
                        },
                        {
                            label: 'Create HASTY Relay',
                            image: '/images/assets/icon-relay-hasty.svg',
                            organizationFeatures: ['ALLOW_RELAYS'],
                            permission: 'create assets',
                            testId: 'create-hasty-relay',
                            command: () =>
                                this.$router.push(
                                    `/${this.organizationSlug}/relays/create?type=${relayTypes.HASTY.type}`
                                ),
                        },
                        {
                            label: 'Create ADVANCED Relay',
                            image: '/images/assets/icon-relay-advanced.svg',
                            featureFlags: [featureFlagConstants.ADVANCED_RELAYS_ENABLED],
                            organizationFeatures: ['ALLOW_RELAYS'],
                            permission: 'create assets',
                            testId: 'create-advanced-relay',
                            command: () =>
                                this.$router.push(
                                    `/${this.organizationSlug}/relays/create?type=${relayTypes.ADVANCED.type}`
                                ),
                        },
                    ],
                },
                {
                    label: 'Radios',
                    testId: 'create-radios-submenu',
                    items: [
                        {
                            label: 'Create Pro X Radio',
                            image: '/images/assets/icon-asset-radio-prox.svg',
                            permission: 'create assets',
                            testId: 'create-radio-pro-x',
                            command: () =>
                                this.$router.push(
                                    `/${this.organizationSlug}/assets/radios/create?type=${radioTypes.PRO_X.type}`
                                ),
                        },
                        {
                            label: 'Create Pro X2 Radio',
                            image: '/images/assets/icon-asset-radio-prox.svg',
                            permission: 'create assets',
                            testId: 'create-radio-pro-x2',
                            command: () =>
                                this.$router.push(
                                    `/${this.organizationSlug}/assets/radios/create?type=${radioTypes.PRO_X2.type}`
                                ),
                        },
                        {
                            label: 'Create NextGen Radio',
                            image: '/images/assets/icon-asset-radio-nextgen.svg',
                            permission: 'create assets',
                            testId: 'create-radio-next-gen',
                            command: () =>
                                this.$router.push(
                                    `/${this.organizationSlug}/assets/radios/create?type=${radioTypes.NEXT_GEN.type}`
                                ),
                        },
                    ],
                },
                {
                    label: 'Bulk',
                    testId: 'create-bulk-submenu',
                    items: [
                        {
                            label: 'Bulk Import Relays',
                            image: '/images/assets/icon-bulk-import-relays.svg',
                            permission: 'upload assets',
                            testId: 'bulk-import-relays',
                            command: () => this.$router.push(`/${this.organizationSlug}/relays/upload`),
                        },
                        {
                            label: 'Bulk Import Radios',
                            image: '/images/assets/icon-bulk-import-radios.svg',
                            permission: 'upload assets',
                            testId: 'bulk-import-radios',
                            command: () => this.$router.push(`/${this.organizationSlug}/asset-radios/upload`),
                        },
                    ],
                },
            ],
        }
    },

    computed: {
        visibleCreateActions() {
            // Filter each section's entries, then drop any section left empty by
            // permissions/feature flags so its header never shows on its own.
            return this.createActionGroups
                .map((group) => ({
                    ...group,
                    items: group.items.filter((action) => this.isActionVisible(action)),
                }))
                .filter((group) => group.items.length > 0)
        },
    },

    mounted() {
        // Work around a PrimeVue bug (primefaces/primevue#7243, #4680): TieredMenu's
        // scrollInView() calls scrollIntoView({ inline: 'start' }) whenever the focused
        // item changes, which scrolls the viewport horizontally and shifts the whole
        // page left. This menu is short and never scrolls internally, so scrollInView
        // only ever causes that harm here — disable it for this instance. (Upstream fix
        // is inline: 'start' -> 'nearest', already applied to MegaMenu.)
        if (this.$refs.createMenu) {
            this.$refs.createMenu.scrollInView = () => {}
        }
    },

    methods: {
        toggleMenu(event) {
            this.$refs.createMenu.toggle(event)
        },
        isActionVisible(action) {
            const { permission, organizationFeatures = [], featureFlags = [] } = action

            return (
                this.can(permission) &&
                this.hasAllOrgFeatures(organizationFeatures) &&
                this.hasAllFeatureFlags(featureFlags)
            )
        },
        hasAllOrgFeatures(features) {
            return features.every((feature) => this.$store.getters.organizationHasFeature(feature))
        },
        hasAllFeatureFlags(flags) {
            return flags.every((flag) => this.$store.getters.hasFeatureFlag(flag))
        },
    },
}
</script>
<style lang="scss" scoped>
.create-asset-button {
    background-color: #59c966 !important;
    border-color: #59c966 !important;
    color: #28522d;
    border-radius: 50px;
    font-size: 0.875rem;
    line-height: 1;
    padding-top: 5px;
    padding-bottom: 5px;
}

.create-asset-menu__image-icon {
    width: 1rem;
    height: 1rem;
    object-fit: contain;
    vertical-align: middle;
}
</style>
